/**
 * Module Studio (UPT-2079 lot L0) — lecture de
 * `GET /api/v1/module-studio/capabilities`, une fois par session (mémoire de
 * module).
 *
 * Relecture du 18.10 (W2) : une erreur (réseau, 5xx, 401, 404…) ne bascule PLUS
 * silencieusement en démonstration. La démonstration n'est active que si le
 * profil Vite le demande (`VITE_MODULE_STUDIO_DEMO=true`) ; sinon la lecture
 * ÉCHOUE (`ModuleStudioUnreachableError`), rien n'est mis en cache et la
 * prochaine lecture réessaie. Le fournisseur affiche alors « Serveur
 * injoignable » avec « Réessayer », sans aucune mutation possible.
 */
import BaseApi from '@/uptk/core/api/BaseApi';
import type { ModuleStudioCapabilities } from './contracts';

/** Le serveur n'a pas pu servir les capacités : rien n'est en démonstration, rien n'est enregistré. */
export class ModuleStudioUnreachableError extends Error {
  readonly status: number | null;

  constructor(cause: unknown) {
    const status = readStatus(cause);
    super(
      status === null
        ? 'module-studio: serveur injoignable (capacités non lues)'
        : `module-studio: serveur injoignable (capacités non lues, HTTP ${status})`
    );
    this.name = 'ModuleStudioUnreachableError';
    this.status = status;
  }
}

function readStatus(cause: unknown): number | null {
  const response = (cause as { response?: { status?: unknown } } | null)?.response;
  return typeof response?.status === 'number' ? response.status : null;
}

/** Profil Vite de démonstration : `VITE_MODULE_STUDIO_DEMO=true` dans le `.env` du poste. */
const readDemoProfile = (): boolean => {
  const env = (import.meta as unknown as { env?: Record<string, unknown> }).env;
  return env?.VITE_MODULE_STUDIO_DEMO === 'true' || env?.VITE_MODULE_STUDIO_DEMO === true;
};

let demoProfileOverride: boolean | null = null;

/** Vrai quand le profil Vite demande explicitement la démonstration (données locales). */
export const isModuleStudioDemoProfile = (): boolean => demoProfileOverride ?? readDemoProfile();

/** Tests : force (ou libère avec `null`) le profil de démonstration. */
export const setModuleStudioDemoProfileForTests = (value: boolean | null): void => {
  demoProfileOverride = value;
};

export const ALL_DEMO_CAPABILITIES: ModuleStudioCapabilities = {
  module: 'module-studio',
  lot: 'demo',
  resources: {
    dossiers: false,
    specs: false,
    previewDatasets: false,
    testPlans: false,
    testRuns: false,
    voice: false,
    prototypeImports: false,
    prototypeAnalysis: false,
    moduleSpec: false,
    designer: false,
    preview: false,
    generator: false,
    generatorStripe: false,
    generatorRepo: false,
    pipeline: false,
    pipelineData: false,
    pipelineGates: false,
    feedback: false,
    deliveryCheck: false,
    presentation: false,
    ideas: false,
    customPages: false,
    tenantModulePresentation: false,
    translation: false,
    simulation: false,
    translationJobs: false,
    capabilityCatalog: false,
    navigationFidelity: false,
    dashboardBlocks: false,
    referenceCaptures: false,
    screenBlocks: false,
    moduleComponents: false,
    computedFields: false,
    conversionJobs: false,
    faithfulState: false,
    protectionConfirmation: false,
  },
};

let cached: ModuleStudioCapabilities | null = null;
let pending: Promise<ModuleStudioCapabilities> | null = null;

const normalize = (value: unknown): ModuleStudioCapabilities => {
  const candidate = (value ?? {}) as Partial<ModuleStudioCapabilities>;
  const resources = (candidate.resources ?? {}) as Partial<
    ModuleStudioCapabilities['resources']
  >;
  return {
    module: 'module-studio',
    lot: typeof candidate.lot === 'string' ? candidate.lot : 'demo',
    resources: {
      ideas: resources.ideas === true,
      dossiers: resources.dossiers === true,
      specs: resources.specs === true,
      previewDatasets: resources.previewDatasets === true,
      testPlans: resources.testPlans === true,
      testRuns: resources.testRuns === true,
      voice: resources.voice === true,
      prototypeImports: resources.prototypeImports === true,
      prototypeAnalysis: resources.prototypeAnalysis === true,
      moduleSpec: resources.moduleSpec === true,
      // UPT-2079 L3 : brouillon du Concepteur et export d'un module installé.
      designer: resources.designer === true,
      // UPT-2079 L4 : jeux de données d'aperçu, génération de lignes, lien de partage (onzième clé).
      preview: resources.preview === true,
      // UPT-2079 L5a : générateur (plan d'artefacts et bundle), douzième clé ; absente = faux.
      generator: resources.generator === true,
      // UPT-2079 L5c : Stripe en mode test, treizième clé ; absente = faux (lue ainsi tant que le serveur ne l'annonce pas).
      generatorStripe: resources.generatorStripe === true,
      // UPT-2079 L5d : dépôt du bundle dans le dépôt git du module, quatorzième clé ; absente = faux (lue ainsi tant que le serveur ne l'annonce pas).
      generatorRepo: resources.generatorRepo === true,
      // UPT-2079 L6a : registre des environnements et suivi CODE en lecture, quinzième clé ; absente = faux (lue ainsi tant que le serveur ne l'annonce pas).
      pipeline: resources.pipeline === true,
      // UPT-2079 L6b : copie de données d'un locataire à un autre (plan, exécution, retour arrière), seizième clé ; absente = faux.
      pipelineData: resources.pipelineData === true,
      // UPT-2079 L6c : conditions avant la copie, approbation, parcours d'un module et ouverture d'un environnement, dix-septième clé ; absente = faux.
      pipelineGates: resources.pipelineGates === true,
      // UPT-2079 L10a : retours de recette classés (saisie, classement, transmission, résolution), dix-huitième clé ; absente = faux (lue par son NOM,
      // l'ordre des clés du JSON n'a aucun sens pour le web : le serveur l'émet à la suite de la dernière clé servie).
      feedback: resources.feedback === true,
      // UPT-2079 L12b : contrôle de livraison anticipé et refus d'« Accepter » (409 `modulespec.delivery_blocked`), dix-neuvième clé ; absente = faux (un serveur plus ancien
      // ne casse rien : la carte « Prêt à livrer ? » est masquée et le reste de l'étape Concevoir reste inchangé).
      deliveryCheck: resources.deliveryCheck === true,
      // UPT-2079 L14a : habillage du module (inventaire v2, ressources image, habillage repris du prototype), vingtième clé ; absente = faux (un serveur plus ancien ne casse rien :
      // le panneau « Habillage », l'onglet « Ressources » et la mesure de style sont masqués, l'inventaire reste en v1).
      presentation: resources.presentation === true,
      // UPT-2079 L14b : pages sur mesure (genre `custom`), servie après `ideas` ; absente = faux (un serveur plus ancien ne casse rien : l'éditeur des pages sur mesure est masqué).
      customPages: resources.customPages === true,
      tenantModulePresentation: resources.tenantModulePresentation === true,
      translation: resources.translation === true,
      simulation: resources.simulation === true,
      // UPT-2079 P2b : traduction en tâche de fond, vingt-sixième clé (après `simulation`) ; absente = faux (le web garde la traduction synchrone de L12c).
      translationJobs: resources.translationJobs === true,
      capabilityCatalog: resources.capabilityCatalog === true,
      // UPT-2079 F2 : navigation fidèle (menu du prototype, proposition, arbre rédigé, menu simulé), vingt-huitième clé lue par son NOM ; absente = faux.
      navigationFidelity: resources.navigationFidelity === true,
      dashboardBlocks: resources.dashboardBlocks === true,
      referenceCaptures: resources.referenceCaptures === true,
      screenBlocks: resources.screenBlocks === true,
      moduleComponents: resources.moduleComponents === true,
      computedFields: resources.computedFields === true,
      conversionJobs: resources.conversionJobs === true,
      faithfulState: resources.faithfulState === true,
      protectionConfirmation: resources.protectionConfirmation === true,
    },
  };
};

/**
 * Lit les capacités (une fois par session). Toute erreur → `ModuleStudioUnreachableError` (rien n'est mis en
 * cache : la prochaine lecture réessaie), sauf si le profil Vite demande la démonstration (tout en démonstration).
 */
export const loadModuleStudioCapabilities = (): Promise<ModuleStudioCapabilities> => {
  if (cached) {
    return Promise.resolve(cached);
  }
  if (!pending) {
    pending = BaseApi.get('/api/v1/module-studio/capabilities')
      .then((response) => normalize(response.data))
      .catch((cause: unknown) => {
        if (isModuleStudioDemoProfile()) {
          return ALL_DEMO_CAPABILITIES;
        }
        throw new ModuleStudioUnreachableError(cause);
      })
      .then(
        (capabilities) => {
          cached = capabilities;
          pending = null;
          return capabilities;
        },
        (error: unknown) => {
          pending = null;
          throw error;
        }
      );
  }
  return pending;
};

/** Tests : oublie la lecture de session. */
export const resetModuleStudioCapabilitiesCache = (): void => {
  cached = null;
  pending = null;
};
